/**
 * Venner · Flags in the country selectors.
 *
 * Puts Shopify's official flag next to each country link in the header and replaces the
 * footer link ("Change country") with the country the visitor is currently shopping in.
 *
 * Works in both stores even though their menus are built differently: one uses a custom
 * selector snippet, the other a regular navigation dropdown. Instead of relying on the
 * markup, each link's country is inferred from its text ("United States (USD)" → US) by
 * matching it against the browser's region names, accent-insensitive, in the page language
 * and in English. That way the menu stays editable from Shopify without typing ISO codes
 * anywhere.
 *
 * Configured through data- attributes on the <script> tag that loads it.
 */

(() => {
  if (window.vennerFlagsReady) return;
  window.vennerFlagsReady = true;

  const config = (document.currentScript && document.currentScript.dataset) || {};
  const CDN = 'https://cdn.shopify.com/static/images/flags/';

  // A dropdown only counts as a "country menu" if several of its links are countries. This
  // keeps a flag off a help menu that happens to mention a country in one of its links.
  const MIN_COUNTRY_LINKS = 2;

  const plain = (value) =>
    value
      .replace(/\s*\([^)]*\)\s*/g, ' ')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  /*
    Name → ISO map built on the fly: we walk all 676 two-letter combinations and keep the
    ones the browser recognizes as a region. No need to ship a country table, and it still
    works if the menu is written in another language.
  */
  let lookup = null;

  function nameToIso(label) {
    if (!lookup) {
      lookup = new Map();
      for (const locale of [document.documentElement.lang || 'en', 'en']) {
        let names;
        try {
          names = new Intl.DisplayNames([locale], { type: 'region' });
        } catch (error) {
          continue;
        }
        for (let a = 65; a <= 90; a += 1) {
          for (let b = 65; b <= 90; b += 1) {
            const iso = String.fromCharCode(a, b);
            const name = names.of(iso);
            if (name && name !== iso) {
              const key = plain(name);
              if (!lookup.has(key)) lookup.set(key, iso);
            }
          }
        }
      }
    }
    return lookup.get(plain(label)) || null;
  }

  function flag(iso) {
    const img = document.createElement('img');
    img.className = 'venner-flag';
    img.src = CDN + iso.toLowerCase() + '.svg';
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 20;
    img.height = 14;
    return img;
  }

  function decorate(anchor, iso) {
    if (anchor.querySelector('.venner-flag')) return;
    anchor.classList.add('venner-flag-link');
    anchor.prepend(flag(iso));
  }

  function enhance() {
    // ---------------------------------------------------------------- header
    document.querySelectorAll('.header__dropdown-menu, .venner-country__menu').forEach((menu) => {
      const found = [];
      menu.querySelectorAll('a').forEach((anchor) => {
        const iso = nameToIso(anchor.textContent);
        if (iso) found.push([anchor, iso]);
      });
      if (found.length >= MIN_COUNTRY_LINKS) {
        found.forEach(([anchor, iso]) => decorate(anchor, iso));
      }
    });

    // ---------------------------------------------------------------- footer
    if (config.footer !== 'true' || !config.iso || !config.country) return;

    document.querySelectorAll('.footer__block a[href*="/pages/markets"]').forEach((link) => {
      if (link.querySelector('.venner-flag')) return;
      link.textContent = '';
      link.classList.add('venner-flag-link', 'venner-flag-link--footer');
      link.append(flag(config.iso), document.createTextNode(config.country));
    });
  }

  /*
    The script loads from the header or from a section in the overlay group, so when it runs
    the browser hasn't parsed the footer or the mobile drawer yet. We have to wait for the
    document to finish, or those elements won't exist yet.
  */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance);
  } else {
    enhance();
  }
})();
